import React from 'react';
import { Product } from '../types';
import MacheteAnatomy from './MacheteAnatomy';

interface SpecMatrixProps {
  products: Product[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SpecMatrix: React.FC<SpecMatrixProps> = ({ products }) => {
  return (
    <section className="bg-paper py-24 lg:py-32">
      <div className="max-w-editorial mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className="mb-16 lg:mb-20 pb-10 border-b border-ink-200">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="max-w-prose">
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-6">
                — Engineering specifications
              </p>
              <h2 className="font-editorial italic font-light text-5xl lg:text-6xl text-ink-950 leading-[1.05] tracking-tight mb-6">
                The spec matrix.
              </h2>
              <p className="text-ink-700 text-lg leading-[1.65]">
                Cross-referencing high-carbon metallurgy, kinetic energy distribution, and
                field-tested performance metrics for the discerning outdoors enthusiast.
              </p>
            </div>

            <dl className="flex flex-wrap gap-x-6 gap-y-3 flex-shrink-0">
              <div className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-ember rounded-full" aria-hidden="true" />
                <span className="font-mono text-[10px] tracking-micro uppercase text-ink-700">
                  Toughness
                </span>
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-forge rounded-full" aria-hidden="true" />
                <span className="font-mono text-[10px] tracking-micro uppercase text-ink-700">
                  Flexibility
                </span>
              </div>
              <div className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-steel rounded-full" aria-hidden="true" />
                <span className="font-mono text-[10px] tracking-micro uppercase text-ink-700">
                  Retention
                </span>
              </div>
            </dl>
          </div>
        </div>

        {/* Anatomy */}
        <MacheteAnatomy />

        {/* Footer notes */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 pt-10 border-t border-ink-200">

          <article className="border-l border-ink-200 pl-6">
            <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">— Material glossary</p>
            <h4 className="font-editorial italic text-xl text-ink-950 mb-3 tracking-tight">
              SAE 1075.
            </h4>
            <p className="text-sm text-ink-700 leading-relaxed">
              High-carbon spring steel characterized by extreme impact resistance and ease of field
              restoration. The standard for Jamaican and Colombian harvest tools.
            </p>
          </article>

          <article className="border-l border-ink-200 pl-6">
            <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">— Cultural respect</p>
            <h4 className="font-editorial italic text-xl text-ink-950 mb-3 tracking-tight">
              We respect the tool.
            </h4>
            <p className="text-sm text-ink-700 leading-relaxed">
              In the Caribbean, the machete is a symbol of work and provision. We teach this respect
              and safety to every new Canadian customer.
            </p>
          </article>

          <article className="bg-forge text-paper p-6 lg:p-7">
            <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-3">— Buying for a business?</p>
            <h4 className="font-editorial italic text-xl text-paper-warm mb-3 tracking-tight">
              Wholesale pricing.
            </h4>
            <p className="text-sm text-paper/70 leading-relaxed">
              Stocking up for a shop or crew? Sign in for wholesale pricing across Canada.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
};

export default SpecMatrix;
